import { useCallback, useEffect, useMemo, useState, useImperativeHandle, forwardRef, useRef } from 'react';
import { useSelector } from 'react-redux'
import {
    useNodesState,
    useEdgesState,
    addEdge,
    Controls,
    Background,
    MiniMap,
    useReactFlow,
    ReactFlow,
    MarkerType,
} from '@xyflow/react';
import ConnectionLine from '../ConnectionLine';
import N8NEdge from '../edge/N8NEdge';
import FullSizeBtn from '../controls/FullSizeBtn';
import { KEYS_OF_DRAFT_DATA, PREFIX_OF_TEMP_NODE } from '../constant';
import '@xyflow/react/dist/style.css';
import '../flow.css'
import useNodeCRUD from '../hooks/useNodeCRUD';
import { Snackbar, Menu, MenuItem, ListItemIcon, ListItemText, Divider, IconButton, Popover, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { getNodeTypesForReactFlow } from '../nodes/registry';
import { getLayoutedElements, initDataFromQuestions } from './canvasUtils';
import NodePalette from '../panels/NodePalette';
import PropertyPanel from '../panels/PropertyPanel';

const fullscreenSx = { position: 'absolute', inset: 0, zIndex: 1000, width: '100vw', height: '100vh', background: '#fff' }
const normalscreenSx = { position: 'relative', width: '99%', height: '99%', border: '1px solid #3333' }

const edgeTypes = { default: N8NEdge }
const CLONE_OFFSET_X = 50
const CLONE_OFFSET_Y = 50

const SurveyCanvas = forwardRef(({
    initList,
}, ref) => {

    const savedSurveyFlow = useSelector(state => state.surveyFlow.ui.surveyFlow)

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const { screenToFlowPosition, getNodes, getEdges, fitView, deleteElements } = useReactFlow();

    const { onNodeClone } = useNodeCRUD()

    const [selectedOption, setSelectedOption] = useState('1');
    const [fullscreen, setFullscreen] = useState(false)
    const [layoutDir, setLayoutDir] = useState('LR')  // 'LR' horizontal | 'TB' vertical
    const [nodeIdsToClone, setNodeIdsToClone] = useState([])
    const [snackbar, setSnackbar] = useState({ open: false, message: '' })

    // Context menu state
    const [contextMenu, setContextMenu] = useState(null)
    const [shortcutsAnchor, setShortcutsAnchor] = useState(null)
    const contextNodeRef = useRef(null)
    const shortcutsBtnRef = useRef(null)

    // node types from registry
    const nodeTypes = useMemo(() => getNodeTypesForReactFlow(), []);

    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge({ ...params, data: { condition: selectedOption } }, eds)),
        [selectedOption]
    );

    const onDrop = useCallback((event) => {
        event.preventDefault();
        const type = event.dataTransfer.getData('application/reactflow');
        const position = screenToFlowPosition({ x: event.clientX, y: event.clientY - 40 });

        const newNode = {
            id: `${PREFIX_OF_TEMP_NODE}${Date.now()}`,
            type,
            position,
            data: {
                id: `${PREFIX_OF_TEMP_NODE}${Date.now()}`,
                sectionId: null,
                question: { EN: type, ZH_CN: type, ZH_HK: type },
                answerLabel: { EN: type, ZH_CN: type, ZH_HK: type },
                type: type,
                inputMode: null,
                inputRegex: null,
                minSize: type === 'RADIO' ? 1 : 0,
                maxSize: type === 'RADIO' ? 1 : 0,
                mandatory: false,
                answerOptionList: null,
                sequence: 10,
                status: 'A',
                parentQuestionId: null,
                nextQuestionMap: null,
                filterKey: null,
            },
        };

        setNodes((nds) => nds.concat(newNode));
    }, [screenToFlowPosition])

    const onDragOver = useCallback((e) => e.preventDefault(), [])

    const handleFullscreen = useCallback(() => setFullscreen(!fullscreen), [fullscreen])

    const cycleDirection = useCallback(() => {
        setLayoutDir(d => d === 'LR' ? 'TB' : 'LR')
    }, [])

    const handleAutoLayout = useCallback(() => {
        const { nodes: layouted, edges: layoutedEdges } = getLayoutedElements(getNodes(), getEdges(), layoutDir)
        setNodes(layouted)
        setEdges(layoutedEdges)
        setTimeout(() => fitView(), 60)
    }, [getNodes, getEdges, layoutDir, setNodes, setEdges, fitView])

    // Track whether the second-pass re-layout has run
    const hasRelayoutRef = useRef(false)

    // Initialize nodes/edges from API questionList
    useEffect(() => {
        if (initList && initList.length > 0) {
            const { nodes: initNodes, edges: initEdges } = initDataFromQuestions(initList);
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(initNodes, initEdges, layoutDir);
            setNodes(layoutedNodes)
            setEdges(layoutedEdges)
            hasRelayoutRef.current = false
            setTimeout(() => fitView(), 60);
        }
    }, [initList, layoutDir, fitView, setEdges, setNodes])

    // Reset re-layout guard when direction or initList changes
    useEffect(() => { hasRelayoutRef.current = false }, [layoutDir, initList])

    // Second-pass re-layout: after ReactFlow measures actual node dimensions,
    // re-run layout with real heights so the 12px gap is exact
    useEffect(() => {
        if (hasRelayoutRef.current) return
        const measured = nodes.filter(n => n.measured?.height)
        if (measured.length === 0) return

        hasRelayoutRef.current = true
        const { nodes: relayouted } = getLayoutedElements(nodes, edges, layoutDir);
        setNodes(relayouted)
        setTimeout(() => fitView(), 30);
    }, [nodes, edges, layoutDir, setNodes, fitView])

    // Persist draft to localStorage
    useEffect(() => {
        localStorage.setItem(KEYS_OF_DRAFT_DATA, JSON.stringify(savedSurveyFlow))
    }, [savedSurveyFlow])

    // ---- Clipboard (multi-node copy/paste) ----

    const onCopy = useCallback(() => {
        const selected = nodes.filter(n => n.selected)
        if (selected.length > 0) {
            setNodeIdsToClone(selected.map(n => n.id))
            setSnackbar({ open: true, message: selected.length === 1 ? 'Copied node' : `Copied ${selected.length} nodes` })
        }
    }, [nodes])

    const onPaste = useCallback(() => {
        if (nodeIdsToClone.length === 0) return
        const allNodes = getNodes()
        const toClone = allNodes.filter(n => nodeIdsToClone.includes(n.id))
        if (toClone.length === 0) return

        const offset = { x: CLONE_OFFSET_X * (nodeIdsToClone.length > 1 ? 0 : 1), y: CLONE_OFFSET_Y }
        const newNodes = toClone.map(n => ({
            ...n,
            id: `${PREFIX_OF_TEMP_NODE}${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            selected: true,
            position: { x: n.position.x + offset.x, y: n.position.y + offset.y },
            data: { ...n.data, id: `${PREFIX_OF_TEMP_NODE}${Date.now()}_${Math.random().toString(36).slice(2, 6)}` },
        }))

        // Deselect existing, add new
        setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), ...newNodes])
        setSnackbar({ open: true, message: newNodes.length === 1 ? 'Pasted node' : `Pasted ${newNodes.length} nodes` })
    }, [nodeIdsToClone, getNodes, setNodes])

    // ---- Duplicate selected (Ctrl+D) ----

    const duplicateSelected = useCallback(() => {
        const selected = nodes.filter(n => n.selected)
        if (selected.length === 0) return

        const newNodes = selected.map((n, i) => ({
            ...n,
            id: `${PREFIX_OF_TEMP_NODE}${Date.now()}_${i}`,
            selected: false,
            position: { x: n.position.x + CLONE_OFFSET_X, y: n.position.y + CLONE_OFFSET_Y },
            data: { ...n.data, id: `${PREFIX_OF_TEMP_NODE}${Date.now()}_${i}` },
        }))

        setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), ...newNodes])
        setSnackbar({ open: true, message: newNodes.length === 1 ? 'Duplicated' : `Duplicated ${newNodes.length} nodes` })
    }, [nodes, setNodes])

    // ---- Context menu ----

    const onNodeContextMenu = useCallback((event, node) => {
        event.preventDefault()
        contextNodeRef.current = node
        setContextMenu({ mouseX: event.clientX, mouseY: event.clientY })
    }, [])

    const handleContextClose = () => setContextMenu(null)

    const handleContextDelete = useCallback(() => {
        if (contextNodeRef.current) {
            deleteElements({ nodes: [{ id: contextNodeRef.current.id }] })
            setSnackbar({ open: true, message: 'Deleted' })
        }
        setContextMenu(null)
    }, [deleteElements])

    const handleContextDuplicate = useCallback(() => {
        if (contextNodeRef.current) {
            const n = contextNodeRef.current
            const newNode = {
                ...n,
                id: `${PREFIX_OF_TEMP_NODE}${Date.now()}`,
                selected: false,
                position: { x: n.position.x + CLONE_OFFSET_X, y: n.position.y + CLONE_OFFSET_Y },
                data: { ...n.data, id: `${PREFIX_OF_TEMP_NODE}${Date.now()}` },
            }
            setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode])
            setSnackbar({ open: true, message: 'Duplicated' })
        }
        setContextMenu(null)
    }, [setNodes])

    const handleContextCopy = useCallback(() => {
        if (contextNodeRef.current) {
            setNodeIdsToClone([contextNodeRef.current.id])
            setSnackbar({ open: true, message: 'Copied' })
        }
        setContextMenu(null)
    }, [])

    // ---- Keyboard shortcuts (Ctrl+D) ----

    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
                e.preventDefault()
                duplicateSelected()
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [duplicateSelected])

    // Expose getNodes/getEdges for parent save
    useImperativeHandle(ref, () => ({
        getNodes,
        getEdges,
    }), [getNodes, getEdges]);

    return (
        <div style={fullscreen ? fullscreenSx : normalscreenSx}>
            <NodePalette />
            <PropertyPanel />

            <ReactFlow
                fitView
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                connectionLineComponent={ConnectionLine}
                defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}
                onDrop={onDrop}
                onDragOver={onDragOver}
                deleteKeyCode={['Delete', 'Backspace']}
                onCopy={onCopy}
                onPaste={onPaste}
                onNodeContextMenu={onNodeContextMenu}
            >
                <Controls showInteractive={false} position="bottom-right">
                    <FullSizeBtn onClick={handleFullscreen} />
                    <IconButton
                        size="small"
                        onClick={cycleDirection}
                        title={`Layout: ${layoutDir === 'LR' ? 'Horizontal' : 'Vertical'} (click to toggle)`}
                        sx={{ width: 28, height: 28, borderRadius: '6px', '&:hover': { background: '#f8fafc' } }}
                    >
                        <AccountTreeIcon sx={{ width: 16, height: 16, color: '#64748b', transform: layoutDir === 'TB' ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s ease' }} />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={handleAutoLayout}
                        title="Auto layout"
                        sx={{ width: 28, height: 28, borderRadius: '6px', '&:hover': { background: '#f8fafc' } }}
                    >
                        <AutoFixHighIcon sx={{ width: 16, height: 16, color: '#64748b' }} />
                    </IconButton>
                    <IconButton
                        ref={shortcutsBtnRef}
                        size="small"
                        onClick={(e) => setShortcutsAnchor(e.currentTarget)}
                        title="Keyboard shortcuts"
                        sx={{ width: 28, height: 28, borderRadius: '6px', '&:hover': { background: '#f8fafc' } }}
                    >
                        <KeyboardIcon sx={{ width: 16, height: 16, color: '#64748b' }} />
                    </IconButton>
                </Controls>
                <MiniMap position="bottom-right" pannable zoomable />
                <Background variant="dots" gap={12} size={1} />
            </ReactFlow>

            <Snackbar
                open={snackbar.open}
                message={snackbar.message}
                autoHideDuration={1200}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transitionDuration={0}
                sx={{
                    '&': { top: '50%', transform: 'translateY(-50%)' },
                    '& .MuiPaper-root': { minWidth: 'auto', backgroundColor: 'rgb(107, 107, 107)', color: '#fff', borderRadius: '6px', px: 1.5 }
                }}
                onClose={() => setSnackbar({ open: false, message: '' })}
            />

            {/* Right-click context menu */}
            <Menu
                open={contextMenu !== null}
                onClose={handleContextClose}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 160, borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0' } } }}
            >
                <MenuItem onClick={handleContextDuplicate} dense>
                    <ListItemIcon><FileCopyIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Duplicate</ListItemText>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 16 }}>Ctrl+D</span>
                </MenuItem>
                <MenuItem onClick={handleContextCopy} dense>
                    <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Copy</ListItemText>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 16 }}>Ctrl+C</span>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleContextDelete} dense>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                    <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 16 }}>Del</span>
                </MenuItem>
            </Menu>

            {/* Keyboard shortcuts popover */}
            <Popover
                open={Boolean(shortcutsAnchor)}
                anchorEl={shortcutsAnchor}
                onClose={() => setShortcutsAnchor(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0', p: 2, maxWidth: 280 } } }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#334155', fontSize: 13 }}>
                    Keyboard Shortcuts
                </Typography>
                {[
                    { key: 'Ctrl+D', desc: 'Duplicate selected node(s)' },
                    { key: 'Ctrl+C', desc: 'Copy selected node(s)' },
                    { key: 'Ctrl+V', desc: 'Paste copied node(s)' },
                    { key: 'Delete / Backspace', desc: 'Delete selected node(s)' },
                    { key: 'Right-click', desc: 'Context menu on node' },
                    { key: 'Drag from palette', desc: 'Add new question node' },
                ].map(({ key, desc }) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <kbd style={{
                            background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4,
                            padding: '1px 6px', fontSize: 11, fontFamily: 'inherit', color: '#475569',
                            fontWeight: 600, minWidth: 24, textAlign: 'center',
                        }}>{key}</kbd>
                        <Typography variant="caption" sx={{ color: '#64748b', ml: 2, flex: 1 }}>{desc}</Typography>
                    </div>
                ))}
            </Popover>
        </div>
    )
});

SurveyCanvas.displayName = 'SurveyCanvas';

export default SurveyCanvas;
